// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Collection tier definitions for community vs premium editions.
 *
 * RxDB free tier limits databases to 13 collections. This module defines
 * which collections are included in the community (free) tier and provides
 * a selector function used by host apps at database initialization time.
 */

import type { CollectionMode } from './config/CollectionModeConfig'

/**
 * Collections included in the free community edition (max 13 for RxDB free tier).
 *
 * These cover core project management, document storage, team collaboration,
 * and the schema/record system needed for virtual collection support.
 */
export const COMMUNITY_COLLECTIONS = [
  'orgs',
  'users',
  'workspaces',
  'projects',
  'milestones',
  'tasks',
  'documents',
  'discussions',
  'teammembers',
  'schemas',
  'records',
  'goals',
  'templates',
] as const;

export type CommunityCollectionName = (typeof COMMUNITY_COLLECTIONS)[number];

/**
 * All 32 core collection names across both community and premium tiers.
 */
export const ALL_COLLECTION_NAMES = [
  ...COMMUNITY_COLLECTIONS,
  'appsnapshots',
  'approvals',
  'activities',
  'attributes',
  'codebases',
  'conversations',
  'messages',
  'processexecutions',
  'processes',
  'processsteps',
  'steptemplates',
  'relations',
  'rolememberships',
  'roles',
  'servicekeys',
  'timeentries',
  'vaultitems',
  'vaults',
  'workers',
] as const;

export type CollectionName = (typeof ALL_COLLECTION_NAMES)[number];

/**
 * Build per-collection mode map based on premium status.
 *
 * - Premium: all 32 collections → 'local' (empty map, defaultMode handles it)
 * - Community: 13 community collections → 'local', remaining 19 → 'remote'
 */
export function buildCollectionModes(
  premium: boolean,
): Record<string, CollectionMode> {
  // Premium: return empty map — defaultMode 'local' handles all
  if (premium) return {}

  const communitySet = new Set<string>(COMMUNITY_COLLECTIONS)
  const result: Record<string, CollectionMode> = {}
  for (const name of ALL_COLLECTION_NAMES) {
    result[name] = communitySet.has(name) ? 'local' : 'remote'
  }
  return result
}

/**
 * Select collections for the active tier.
 *
 * @param allCollections - Full collection map (all 32 collections)
 * @param premium - Whether premium edition is active
 * @returns Filtered collection map (13 for free, all for premium)
 */
export function getCollectionsForTier(
  allCollections: Record<string, any>,
  premium: boolean,
): Record<string, any> {
  if (premium) return allCollections;

  const allowed = new Set<string>(COMMUNITY_COLLECTIONS);
  const result: Record<string, any> = {};
  for (const name of allowed) {
    if (allCollections[name]) {
      result[name] = allCollections[name];
    }
  }
  return result;
}
